// One opportunity as it stands for one client (PRAXIS Customer Profile Dev §3-4): what it is, why Praxis
// selected it, where it came from, the relevance assessment NTN can edit, the decision and the notes.
// With clientView the same page renders for a client login: read-only, NTN-internal notes and the decision
// trail hidden, and a feedback form in their place. The API strips that material; the flag shapes the page.
import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../lib/api';
import type { Assessment, Me, OpportunityDetail, Tag } from '../lib/api';
import { daysUntil, fmtDate, fmtDateTime, titleCase } from '../lib/format';
import { Empty, PageHead, Panel, Pill } from '../components/ui';
import { TagChips } from '../components/tags';
import { BandPill, DECISION_LABEL, DecisionButtons, DecisionPill, MatchFlags, ScoreBadge } from '../components/matches';
import { ScorePanel } from '../components/ScorePanel';

const NOTE_KINDS = ['note', 'next_step', 'client_feedback', 'bid_decision', 'teaming', 'strategic_fit', 'competitiveness', 'follow_up', 'recommendation'];
const SECTIONS: { key: keyof Assessment; label: string; clientLabel: string }[] = [
  { key: 'why', label: 'Why it matches the client', clientLabel: 'Why it matches you' }, { key: 'implication', label: 'What it could mean', clientLabel: 'What it could mean' },
  { key: 'gaps', label: 'Gaps and considerations', clientLabel: 'Gaps and considerations' }, { key: 'next_steps', label: 'Next steps for NTN', clientLabel: 'Suggested next steps' },
];

export function OpportunityPage({ clientId, matchId, me, clientView = false }: { clientId: string; matchId: string; me: Me; clientView?: boolean }) {
  const cv = clientView;
  const [d, setD] = useState<OpportunityDetail | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Assessment>({ why: '', implication: '', gaps: '', next_steps: '' });
  const [note, setNote] = useState('');
  const [kind, setKind] = useState('note');
  const [assessNote, setAssessNote] = useState<string | null>(null);

  const load = useCallback(() => {
    api<OpportunityDetail>(`/api/matches/${matchId}`).then((r) => { setD(r); setErr(null); }).catch((e: Error) => setErr(e.message));
  }, [matchId]);
  useEffect(() => { load(); api<Tag[]>('/api/tags').then(setTags).catch(() => setTags([])); }, [load]);

  const canEdit = !cv && me.role !== 'viewer';
  const backHref = cv ? '#/dashboard' : `#/clients/${clientId}`;

  // First visit with no assessment: write one straight away so the page never shows an empty box.
  useEffect(() => {
    if (!d || d.assessment || !canEdit || busy) return;
    void generate('auto');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d?.id, d?.assessment]);

  async function generate(mode: 'auto' | 'rules') {
    setBusy('assess');
    setAssessNote(null);
    try {
      const r = await api<{ assessment_source: string; error: string | null }>(`/api/matches/${matchId}/assess${mode === 'rules' ? '?mode=rules' : ''}`, { method: 'POST' });
      if (r.error) setAssessNote(`Claude was not available (${r.error}); showing the rules-based summary.`);
      load();
    } catch (ex) { setErr((ex as Error).message); } finally { setBusy(null); }
  }
  async function saveAssessment(e: FormEvent) {
    e.preventDefault();
    setBusy('save');
    try {
      await api(`/api/matches/${matchId}/assessment`, { method: 'PATCH', json: draft });
      setEditing(false);
      load();
    } catch (ex) { setErr((ex as Error).message); } finally { setBusy(null); }
  }
  async function addNote(e: FormEvent) {
    e.preventDefault();
    if (!note.trim()) return;
    setBusy('note');
    try {
      await api(`/api/matches/${matchId}/notes`, { method: 'POST', json: cv ? { body: note.trim() } : { body: note.trim(), kind } });
      setNote('');
      load();
    } catch (ex) { setErr((ex as Error).message); } finally { setBusy(null); }
  }

  if (err && !d) return <><PageHead title="Opportunity" /><Empty>{err === 'not_found' ? (cv ? 'This opportunity is not available on your dashboard.' : 'This opportunity is not matched to a client, or was removed.') : err}</Empty></>;
  if (!d) return <><PageHead title="Loading…" /><Empty>Loading…</Empty></>;

  const days = daysUntil(d.response_due_at);
  const agency = d.issuing_org_path?.split('.').filter(Boolean) ?? [];
  const sourceLabel = cv
    ? (d.assessment_source === 'analyst' ? 'reviewed by NTN' : d.assessment_source ? 'prepared by NTN' : 'not yet assessed')
    : d.assessment_source === 'ai' ? `written by Claude${d.assessment_model ? ` (${d.assessment_model})` : ''}`
    : d.assessment_source === 'analyst' ? `edited by ${d.edited_by ?? 'NTN'}` : d.assessment_source === 'rules' ? 'rules-based summary' : 'not yet assessed';

  return (
    <>
      <PageHead
        title={d.title}
        sub={`${cv ? '' : `${d.client_name} · `}${titleCase(d.notice_type)} · ${agency.slice(-1)[0] ?? 'Unknown office'}${d.solicitation_number ? ` · ${d.solicitation_number}` : ''}`}
        actions={
          <>
            <ScoreBadge score={d.score} title="Fit score" /><BandPill band={d.band} total={d.priority_total} /><MatchFlags m={d} />
            {canEdit ? <DecisionButtons matchId={d.id} decision={d.decision} onChanged={load} /> : <DecisionPill decision={d.decision} client={cv} />}
            {d.url && <a className="primary-btn" href={d.url} target="_blank" rel="noreferrer">Open at source ↗</a>}
            <a className="ghost-btn" href={backHref}>← {cv ? 'Your dashboard' : d.client.name}</a>
          </>
        }
      />
      {err && <div className="error-text">{err}</div>}

      <div className="grid grid-2">
        <Panel title="The opportunity" action={<Pill kind={d.is_open ? 'ok' : 'muted'}>{d.is_open ? 'Open' : 'Closed'}</Pill>}>
          <dl className="kv">
            <dt>Agency</dt><dd>{agency.length ? agency.join(' › ') : '—'}</dd>
            <dt>Type</dt><dd>{titleCase(d.notice_type)}</dd>
            <dt>Source</dt><dd>{d.url ? <a className="link" href={d.url} target="_blank" rel="noreferrer">{d.channel.toUpperCase()} · {d.url}</a> : d.channel.toUpperCase()}</dd>
            <dt>Number</dt><dd>{d.solicitation_number ?? '—'}</dd>
            <dt>NAICS / PSC</dt><dd>{d.naics ?? '—'} / {d.psc ?? '—'}</dd>
            <dt>Set-aside</dt><dd>{d.set_aside ?? 'None'}</dd>
            <dt>Posted</dt><dd>{fmtDate(d.posted_at)}</dd>
            <dt>Deadline</dt><dd>{d.response_due_at ? <span className={days !== null && days >= 0 && days <= 7 ? 'neg' : ''}>{fmtDateTime(d.response_due_at)}{days !== null ? (days < 0 ? ` (closed ${-days} days ago)` : ` (${days} days left)`) : ''}</span> : 'No deadline posted'}</dd>
            <dt>Archive</dt><dd>{fmtDate(d.archive_at)}</dd>
            <dt>Technology</dt><dd>{d.tags.length ? <TagChips tags={d.tags} all={tags} max={8} showConfidence /> : '—'}</dd>
            <dt>Tracked</dt><dd>since {fmtDateTime(d.first_seen_at)} · last confirmed {fmtDateTime(d.last_seen_at)} · {d.versions.length} version{d.versions.length === 1 ? '' : 's'}</dd>
          </dl>
        </Panel>
        <Panel title={cv ? 'Why Praxis selected this for you' : `Why Praxis selected this for ${d.client.name}`} action={<span className="muted sm">identified {fmtDateTime(d.created_at)}</span>}>
          <RationaleList d={d} tags={tags} />
          <div className="muted sm" style={{ marginTop: 12 }}>
            Scored against {cv ? 'your' : "the profile's"} technology focus, NAICS codes, target agencies, keywords and set-asides.
            {!cv && <> <a className="link" href={backHref}>Edit the profile</a> to change what counts.</>}
          </div>
          {d.other_clients.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div className="muted sm" style={{ marginBottom: 4 }}>Also fits</div>
              {d.other_clients.map((o) => (
                <div className="version" key={o.id}><time><ScoreBadge score={o.score} /></time><span style={{ flex: 1 }}><a className="link" href={`#/clients/${o.client_id}/opportunity/${o.id}`}>{o.client_name}</a></span>{o.decision !== 'pending' && <DecisionPill decision={o.decision} />}</div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Panel
        title="Priority score"
        action={<span className="muted sm">{cv ? 'NTN\'s priority model' : `Applicability ${d.weights.applicability} · Access ${d.weights.access} · Magnitude ${d.weights.magnitude} · Complexity ${d.weights.complexity}`}{d.scores?.computed_at ? ` · ${fmtDateTime(d.scores.computed_at)}` : ''}</span>}
      >
        <ScorePanel matchId={d.id} scores={d.scores} weights={d.weights} canEdit={canEdit} clientView={cv} decision={d.decision} onChanged={load} />
      </Panel>

      <Panel
        title="Relevance assessment"
        action={
          <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
            <span className="muted sm">{sourceLabel}{d.assessed_at ? ` · ${fmtDateTime(d.assessed_at)}` : ''}</span>
            {canEdit && !editing && d.assessment && <button className="ghost-btn" onClick={() => { setDraft(d.assessment!); setEditing(true); }}>Edit</button>}
            {canEdit && !editing && <button className="ghost-btn" disabled={busy === 'assess'} onClick={() => generate('auto')}>{busy === 'assess' ? 'Writing…' : d.assessment ? 'Regenerate' : 'Generate'}</button>}
            {canEdit && !editing && d.assessment && d.assessment_source !== 'rules' && <button className="ghost-btn" disabled={busy === 'assess'} onClick={() => generate('rules')} title="Deterministic summary from the fit rationale, no model">Rules only</button>}
          </span>
        }
      >
        {assessNote && <div className="muted sm" style={{ marginBottom: 10 }}>{assessNote}</div>}
        {editing ? (
          <form onSubmit={saveAssessment} className="assessment">
            {SECTIONS.map((s) => (
              <div className="field" key={s.key}><label htmlFor={`as-${s.key}`}>{s.label}</label><textarea id={`as-${s.key}`} className="textarea" value={draft[s.key]} onChange={(e) => setDraft({ ...draft, [s.key]: e.target.value })} /></div>
            ))}
            <div className="form-actions">
              <button type="button" className="ghost-btn" onClick={() => setEditing(false)}>Cancel</button>
              <button type="submit" className="primary-btn" disabled={busy === 'save' || SECTIONS.some((s) => !draft[s.key].trim())}>{busy === 'save' ? 'Saving…' : 'Save assessment'}</button>
            </div>
          </form>
        ) : !d.assessment ? (
          <Empty>{busy === 'assess' ? 'Writing the assessment…' : cv ? 'NTN has not assessed this opportunity yet. Check back after the next review.' : canEdit ? 'No assessment yet. Generate one from the profile and the notice.' : 'No assessment yet.'}</Empty>
        ) : (
          <div className="assessment">
            {SECTIONS.map((s) => <div key={s.key}><h4>{cv ? s.clientLabel : s.label}</h4><p>{d.assessment![s.key]}</p></div>)}
          </div>
        )}
      </Panel>

      <div className="grid grid-2" style={{ marginTop: 18 }}>
        <Panel title={cv ? `Notes from NTN and your feedback (${d.notes.length})` : `NTN notes (${d.notes.length})`}>
          {d.notes.length === 0 && <div className="muted sm">{cv ? 'Nothing shared on this opportunity yet. Tell NTN below whether it is of interest.' : 'No notes yet. Decisions, client feedback and next steps recorded here inform future monitoring.'}</div>}
          {d.notes.map((n) => (
            <div className="note" key={n.id}>
              <div><span className="tag new" style={{ marginLeft: 0, marginRight: 8 }}>{titleCase(n.kind).toUpperCase()}</span>{n.body}</div>
              <div className="meta">{n.author ?? (cv ? 'NTN' : 'Unknown')} · {fmtDateTime(n.created_at)}{cv ? '' : ` · ${n.target_type === 'solicitation' ? 'on the notice (all clients)' : `for ${d.client.name}`}${n.visibility === 'client' ? ' · visible to client' : ''}`}</div>
            </div>
          ))}
          {cv ? (
            <form onSubmit={addNote} style={{ marginTop: 12 }}>
              <div className="field"><label htmlFor="on-body">Send feedback to NTN about this opportunity</label><textarea id="on-body" className="textarea" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Interested, not a fit, questions, who should be involved…" /></div>
              <div className="form-actions"><button className="primary-btn" type="submit" disabled={busy === 'note' || !note.trim()}>{busy === 'note' ? 'Sending…' : 'Send to NTN'}</button></div>
            </form>
          ) : canEdit && (
            <form onSubmit={addNote} style={{ marginTop: 12 }}>
              <div className="form-grid">
                <div className="field span-2"><label htmlFor="on-body">Add a note for {d.client.name}</label><textarea id="on-body" className="textarea" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Decision rationale, client feedback, teaming targets, next steps…" /></div>
                <div className="field"><label htmlFor="on-kind">Kind</label>
                  <select id="on-kind" className="select" value={kind} onChange={(e) => setKind(e.target.value)}>
                    {NOTE_KINDS.map((k) => <option key={k} value={k}>{titleCase(k)}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-actions"><button className="primary-btn" type="submit" disabled={busy === 'note' || !note.trim()}>{busy === 'note' ? 'Saving…' : 'Save note'}</button></div>
            </form>
          )}
        </Panel>
        <Panel title={cv ? 'Status and notice history' : 'Decisions and history'}>
          <div className="muted sm" style={{ marginBottom: 6 }}>{cv ? 'NTN status' : 'Current decision'}: <DecisionPill decision={d.decision} client={cv} />{d.decided_at ? ` since ${fmtDateTime(d.decided_at)}` : ''}</div>
          {!cv && (d.decisions.length === 0 ? <div className="muted sm">No decision recorded yet.</div> : d.decisions.map((x, i) => (
            <div className="version" key={i}><time>{fmtDateTime(x.changed_at)}</time><span>{DECISION_LABEL[x.decision] ?? titleCase(x.decision)}{x.actor ? ` by ${x.actor}` : ''}</span></div>
          )))}
          <h3 style={{ fontSize: 13, marginTop: 18, marginBottom: 6 }}>Notice versions ({d.versions.length})</h3>
          {d.versions.map((v, i) => (
            <div className="version" key={v.id}>
              <time>{fmtDateTime(v.observed_at)}</time>
              <span>{i === d.versions.length - 1 ? 'First observed' : v.changed_fields.length ? `Changed: ${v.changed_fields.map(titleCase).join(', ')}` : 'Re-observed'}</span>
            </div>
          ))}
          {!cv && <div className="muted sm" style={{ marginTop: 10 }}>Shared notice history: <a className="link" href={`#/solicitations/id/${d.solicitation_id}`}>open in Solicitations</a>.</div>}
        </Panel>
      </div>
    </>
  );
}

function RationaleList({ d, tags }: { d: OpportunityDetail; tags: Tag[] }) {
  const r = d.rationale;
  if (!r) return <div className="muted sm">No scoring detail stored.</div>;
  const rows: { label: string; detail: string; points: number }[] = [];
  for (const t of r.tags ?? []) rows.push({ label: 'Technology', detail: `${tags.find((x) => x.id === t.tag_id)?.label ?? t.tag_id} (${Math.round(t.confidence * 100)}% confidence)`, points: t.points });
  for (const k of r.keywords ?? []) rows.push({ label: 'Keyword', detail: `“${k.phrase}” in the title`, points: k.points });
  if (r.naics?.match) rows.push({ label: 'NAICS', detail: `${r.naics.notice} ${r.naics.match === 'exact' ? 'is in the profile' : 'is in the profile\'s sector'}`, points: r.naics.points });
  else if (r.naics?.notice) rows.push({ label: 'NAICS', detail: `${r.naics.notice} is not in the profile`, points: 0 });
  if (r.agency?.matched) rows.push({ label: 'Agency', detail: `${titleCase(r.agency.matched.toLowerCase())} is a target agency`, points: r.agency.points });
  else rows.push({ label: 'Agency', detail: 'issuing office is not a listed target agency', points: 0 });
  if (r.set_aside?.status === 'eligible') rows.push({ label: 'Set-aside', detail: `${r.set_aside.notice} held`, points: r.set_aside.points });
  if (r.set_aside?.status === 'ineligible') rows.push({ label: 'Set-aside', detail: `${r.set_aside.notice} not held`, points: r.set_aside.points });
  if (r.notice_type?.points) rows.push({ label: 'Notice type', detail: titleCase(r.notice_type.type), points: r.notice_type.points });
  if (r.title_pattern?.dla_item) rows.push({ label: 'Title', detail: 'DLA catalogue item buy', points: r.title_pattern.points });
  return (
    <table className="data-table">
      <thead><tr><th>Evidence</th><th>Detail</th><th className="num">Points</th></tr></thead>
      <tbody>
        {rows.map((x, i) => <tr key={i}><td>{x.label}</td><td>{x.detail}</td><td className={`num ${x.points > 0 ? 'pos' : x.points < 0 ? 'neg' : 'muted'}`}>{x.points > 0 ? `+${x.points}` : x.points}</td></tr>)}
        <tr><td><strong>Fit score</strong></td><td /><td className="num"><strong>{d.score === null ? '—' : Math.round(d.score)}</strong></td></tr>
      </tbody>
    </table>
  );
}
